import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import ActivePickerTrapContext from './dialogs/ActivePickerTrapContext';

/**
 *  MUI DatePicker with moment-timezone adapter
 * @template {import('@mui/x-date-pickers').PickerComponent} T
 * @param {{Picker: T} | ({Picker: T} & Parameters<T>[0])} props - Combined props
 * @returns {JSX.Element} The localized date picker
 * @see https://mui.com/x/api/date-pickers/date-picker/
 */
const LocalizedDatePicker = ({ Picker = DateTimePicker, onOpen, onClose, ...props }) => {
    // PFM-3150: tell an owning v4 dialog (if any) that this picker's v5 trap is open.
    const pickerTrap = useContext(ActivePickerTrapContext);
    const isOpenRef = useRef(false);

    const setTrapOpen = useCallback(
        (isOpen) => {
            if (!pickerTrap || isOpenRef.current === isOpen) return;
            isOpenRef.current = isOpen;
            pickerTrap.setPickerOpen(isOpen);
        },
        [pickerTrap]
    );

    // Release the outer trap if unmounted while open.
    useEffect(() => () => setTrapOpen(false), [setTrapOpen]);

    const handleOpen = (...args) => {
        setTrapOpen(true);
        onOpen?.(...args);
    };

    const handleClose = (...args) => {
        setTrapOpen(false);
        onClose?.(...args);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Picker {...props} onOpen={handleOpen} onClose={handleClose} />
        </LocalizationProvider>
    );
};

export default LocalizedDatePicker;
